import { createTheme } from "@mui/material";
import { getColor } from "../AppTheme";

export const SERIF = "'Newsreader', Georgia, serif";
export const SANS = "'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif";

/**
 * Tessera design system: ink on paper, thin warm-grey borders, small radii
 * (3–6px), Newsreader serif for display headlines and Hanken Grotesk for
 * everything else. Primary actions are solid black; no heavy shadows.
 */
export default function initTheme(mode = "light") {
    const isDark = mode === "dark";
    const border = getColor("border") || "#E7E5E1";
    const softShadow = isDark
        ? "0 1px 2px rgba(0,0,0,0.4)"
        : "0 1px 2px rgba(23,23,21,0.04)";

    return createTheme({
        mode,
        palette: {
            mode: isDark ? "dark" : "light",
            primary: {
                main: getColor("primary"),
                contrastText: getColor("primary-contrast"),
            },
            secondary: { main: getColor("secondary") },
            success: { main: getColor("success") },
            warning: { main: getColor("warning") },
            error: { main: getColor("error") },
            info: { main: getColor("info") },
            divider: border,
            background: {
                default: getColor("bg"),
                paper: getColor("bg-secondary"),
            },
            text: {
                primary: getColor("text"),
                secondary: getColor("text-secondary"),
            },
        },
        typography: {
            fontFamily: SANS,
            /* Display headlines wear the serif. */
            h3: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-1px" },
            h4: { fontFamily: SERIF, fontWeight: 400, letterSpacing: "-0.5px" },
            h5: { fontFamily: SERIF, fontWeight: 500, letterSpacing: "-0.5px" },
            h6: { fontWeight: 600 },
            subtitle1: { fontWeight: 500 },
            button: { fontWeight: 600 },
        },
        shape: { borderRadius: 3 },
        components: {
            MuiButton: {
                defaultProps: { disableElevation: true },
                styleOverrides: {
                    root: {
                        textTransform: "none",
                        fontWeight: 600,
                        letterSpacing: "0.3px",
                        borderRadius: 3,
                        padding: "8px 20px",
                    },
                },
            },
            MuiCard: {
                defaultProps: { elevation: 0 },
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        border: `1px solid ${getColor("border-mid")}`,
                        boxShadow: softShadow,
                        backgroundImage: "none",
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: { borderRadius: 6 },
                    outlined: { borderColor: border },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: { borderRadius: 6, backgroundImage: "none" },
                },
            },
            MuiChip: {
                styleOverrides: {
                    root: { borderRadius: 3, fontWeight: 600 },
                    sizeSmall: { fontSize: 11, letterSpacing: "0.5px" },
                },
            },
            MuiTextField: {
                defaultProps: { variant: "outlined", size: "small" },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 3,
                        background: getColor("bg-secondary"),
                        "& fieldset": {
                            borderColor: getColor("border-strong"),
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: { borderColor: border },
                    head: {
                        fontWeight: 700,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: "1.5px",
                        color: getColor("text-tertiary"),
                        background: getColor("bg-tint-soft"),
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        borderRadius: 6,
                        border: `1px solid ${getColor("border-mid")}`,
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        border: "none",
                        boxShadow: "1px 0 3px rgba(0,0,0,0.04)",
                    },
                },
            },
            MuiTooltip: {
                styleOverrides: {
                    tooltip: {
                        borderRadius: 3,
                        fontSize: 12,
                        padding: "6px 10px",
                        background: getColor("text"),
                        color: getColor("bg"),
                    },
                },
            },
        },
    });
}
